import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Play,
  ArrowUpRight,
  Sparkles,
  BookOpen,
  Star,
  Globe2,
  Wifi,
  Clock,
  MapPin,
  MessageCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import RightAdRail from "@/components/RightAdRail";
import { useMember } from "@/lib/member";

const HERO_BLOG_CATEGORY_COLOR = {
  Speaking: "bg-[#C7DCF7] text-[#1F2547]",
  Writing: "bg-[#D4EDDB] text-[#1F2547]",
  Grammar: "bg-[#9F87D8] text-white",
  Reading: "bg-[#EFE9F7] text-[#1F2547]",
  Listening: "bg-[#FFE4F1] text-[#1F2547]",
};

const COURSE_TINT = [
  { bg: "bg-[#C7DCF7]", text: "text-[#1F2547]" },
  { bg: "bg-[#D4EDDB]", text: "text-[#1F2547]" },
  { bg: "bg-[#9F87D8]", text: "text-white" },
  { bg: "bg-[#EFE9F7]", text: "text-[#1F2547]" },
  { bg: "bg-[#1F2547]", text: "text-white" },
];

function OffersSidebar({ offers }) {
  if (!offers.length) return null;
  return (
    <aside
      data-testid="home-offers-sidebar"
      className="space-y-5 lg:sticky lg:top-24 self-start"
    >
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-[#9F87D8] animate-pulse"></span>
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#9F87D8]">
          This month
        </span>
      </div>

      {offers.map((o) => (
        <div
          key={o.id}
          data-testid={`offer-card-${o.id}`}
          className="rounded-2xl bg-white brutal-shadow overflow-hidden hover-pop"
        >
          {o.image && (
            <div className="aspect-[16/9] overflow-hidden bg-[#EFE9F7]">
              <img src={o.image} alt={o.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-5">
            <div className="flex items-center gap-2 mb-2">
              {o.discount && (
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full bg-[#9F87D8] text-white">
                  {o.discount}
                </span>
              )}
              {o.expiry && (
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5B6079]">
                  {o.expiry}
                </span>
              )}
            </div>
            <h3 className="font-display font-bold text-lg text-[#1F2547] leading-snug">
              {o.title}
            </h3>
            <p className="mt-1.5 text-sm text-[#5B6079] font-medium">
              {o.description}
            </p>
            {o.link && (
              <a
                href={o.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`offer-cta-${o.id}`}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#1F2547] text-white text-sm font-bold rounded-full btn-press"
              >
                {o.button_text || "Know more"} <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </div>
      ))}
    </aside>
  );
}

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [offers, setOffers] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const { member, signIn } = useMember();

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/offers").then((r) => setOffers(r.data)).catch(() => {});
    api.get("/blogs").then((r) => setBlogs(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page" className="overflow-hidden">
      <RightAdRail />

      {/* JOIN BANNER */}
      {!member && (
        <section
          data-testid="home-join-banner"
          className="bg-[#1F2547] text-white"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
            <div className="flex -space-x-2">
              {["bg-[#9F87D8]", "bg-[#C7DCF7]", "bg-[#D4EDDB]"].map((c, i) => (
                <div key={i} className={`w-8 h-8 rounded-full ${c} ring-2 ring-[#1F2547]`}></div>
              ))}
            </div>
            <div className="flex-1 text-sm md:text-base font-medium">
              <b className="font-display font-black">Join 2,400+ learners</b> —
              bookmark lessons, track downloads, unlock premium content.
            </div>
            <button
              type="button"
              onClick={signIn}
              data-testid="home-join-signin"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#9F87D8] text-white text-sm font-bold rounded-full btn-press whitespace-nowrap"
            >
              Sign in with Google
            </button>
          </div>
        </section>
      )}
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6 float-up">
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white brutal-shadow-sm text-xs font-bold uppercase tracking-[0.2em] text-[#1F2547]"
            >
              <Star className="w-3.5 h-3.5 fill-[#9F87D8] text-[#9F87D8]" /> Step-up in
              the right direction
            </span>
            <h1 className="font-display text-[44px] md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mt-6 text-[#1F2547]">
              Speak it.{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Write it.</span>
                <span className="absolute inset-x-0 bottom-2 h-3 md:h-4 bg-[#D4EDDB] -z-0 -skew-x-6"></span>
              </span>{" "}
              <span className="text-[#9F87D8]">Own it.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#5B6079] font-medium max-w-xl leading-relaxed">
              Modern English coaching for curious learners. Watch our welcome
              note, then pick a course built for the way you actually learn.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/courses"
                data-testid="hero-cta-explore"
                className="px-6 py-3.5 bg-[#9F87D8] text-white font-bold rounded-full brutal-shadow btn-press inline-flex items-center gap-2"
              >
                Explore Courses <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/blogs"
                data-testid="hero-cta-blogs"
                className="px-6 py-3.5 bg-white text-[#1F2547] font-bold rounded-full brutal-shadow-sm btn-press inline-flex items-center gap-2"
              >
                Read the Blog <BookOpen className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-6">
              <div className="flex -space-x-3">
                {["bg-[#9F87D8]", "bg-[#C7DCF7]", "bg-[#D4EDDB]", "bg-[#1F2547]"].map(
                  (c, i) => (
                    <div
                      key={i}
                      className={`w-10 h-10 rounded-full ${c} ring-2 ring-white`}
                    ></div>
                  ),
                )}
              </div>
              <div className="text-sm font-medium text-[#5B6079]">
                <span className="font-bold text-[#1F2547]">2,400+ learners</span>{" "}
                already speaking with confidence.
              </div>
            </div>
          </div>

          {/* Video Placeholder */}
          <div
            className="lg:col-span-6 float-up"
            style={{ animationDelay: "0.15s" }}
          >
            <div
              data-testid="welcome-video-placeholder"
              className="relative w-full aspect-video rounded-3xl brutal-shadow-lg overflow-hidden bg-[#EFE9F7] group cursor-pointer"
            >
              <img
                src="https://images.pexels.com/photos/1774851/pexels-photo-1774851.jpeg"
                alt="Welcome video poster"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1F2547]/35 via-transparent to-transparent"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <button
                  data-testid="play-welcome-video-btn"
                  aria-label="Play welcome video"
                  className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-full brutal-shadow flex items-center justify-center group-hover:scale-110 transition-transform"
                >
                  <Play className="w-8 h-8 md:w-10 md:h-10 text-[#9F87D8] fill-[#9F87D8] ml-1" />
                </button>
              </div>
              <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/90 backdrop-blur text-[#1F2547] text-[10px] font-bold uppercase tracking-[0.2em] rounded-full">
                Welcome • 02:15
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3 text-sm text-[#5B6079] font-medium">
              <Sparkles className="w-4 h-4 text-[#9F87D8]" />A 2-minute intro to
              how Percept Academy works.
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="bg-[#1F2547] py-4 overflow-hidden">
          <div className="marquee-track items-center text-[#D4EDDB] font-display font-black text-2xl md:text-3xl tracking-tight">
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex items-center gap-10 pr-10">
                {[
                  "Speak with confidence",
                  "★",
                  "Write with clarity",
                  "★",
                  "Read with depth",
                  "★",
                  "Think in English",
                  "★",
                ].map((t, i) => (
                  <span key={i} className={t === "★" ? "text-[#C7DCF7]" : ""}>
                    {t}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COURSES + OFFERS SIDEBAR */}
      <section
        id="courses"
        className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-[#9F87D8] mb-3">
              / Courses
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-[#1F2547] max-w-2xl">
              Courses offered by Percept Academy
            </h2>
          </div>
          <p className="text-[#5B6079] font-medium max-w-md text-lg">
            Programs crafted for every stage — from primary school to working
            professionals.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-8 space-y-6">
            {courses.length === 0 && (
              <div className="text-[#5B6079] font-medium">Loading courses…</div>
            )}
            {courses.slice(0, 3).map((c, idx) => {
              const tint = COURSE_TINT[idx % COURSE_TINT.length];
              const isDark = tint.text === "text-white";
              return (
                <article
                  key={c.id}
                  data-testid={`course-card-${c.id}`}
                  className={`group rounded-3xl brutal-shadow hover-pop overflow-hidden ${tint.bg} ${tint.text}`}
                >
                  <div className="grid md:grid-cols-5 gap-0">
                    {c.image && (
                      <div className="md:col-span-2 aspect-[5/4] md:aspect-auto overflow-hidden bg-[#EFE9F7]">
                        <img
                          src={c.image}
                          alt={c.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div
                      className={`${c.image ? "md:col-span-3" : "md:col-span-5"} p-6 md:p-8 flex flex-col justify-between gap-5`}
                    >
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-3">
                          {c.tag && (
                            <span
                              className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full ${isDark ? "bg-white/20 backdrop-blur text-white" : "bg-white/70 backdrop-blur text-[#1F2547]"}`}
                            >
                              {c.tag}
                            </span>
                          )}
                          {c.duration && (
                            <span
                              className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full ${isDark ? "bg-white/20 backdrop-blur text-white" : "bg-[#1F2547] text-white"}`}
                            >
                              {c.duration}
                            </span>
                          )}
                        </div>
                        <h3 className="font-display text-2xl md:text-3xl font-black tracking-tight leading-tight">
                          {c.title}
                        </h3>
                        <p
                          className={`mt-3 text-sm md:text-base font-medium ${isDark ? "text-white/85" : "text-[#1F2547]/75"}`}
                        >
                          {c.description}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <Link
                          to={`/enroll?course=${c.id}`}
                          data-testid={`course-enrol-${c.id}`}
                          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-full btn-press ${isDark ? "bg-white text-[#1F2547]" : "bg-[#1F2547] text-white"}`}
                        >
                          Enrol now <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        {c.whatsapp_link && (
                          <a
                            href={c.whatsapp_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`course-whatsapp-${c.id}`}
                            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-full ${isDark ? "bg-white/15 backdrop-blur text-white" : "bg-white text-[#1F2547] border border-[#1F2547]/10"}`}
                          >
                            <MessageCircle className="w-4 h-4" /> WhatsApp
                          </a>
                        )}
                        {c.offer_link && (
                          <a
                            href={c.offer_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`course-offer-${c.id}`}
                            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-full ${isDark ? "bg-white/15 backdrop-blur text-white" : "bg-[#D4EDDB] text-[#1F2547]"}`}
                          >
                            View offer
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}

            {courses.length > 3 && (
              <div className="pt-2">
                <Link
                  to="/courses"
                  data-testid="home-courses-explore-more"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#1F2547] text-white font-bold rounded-full brutal-shadow btn-press"
                >
                  Explore more courses <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <OffersSidebar offers={offers} />
          </div>
        </div>
      </section>

      {/* LEARN FROM ANYWHERE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div
              data-testid="learn-anywhere-visual"
              className="relative aspect-[5/4] rounded-3xl bg-[#EFE9F7] brutal-shadow overflow-hidden"
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute w-[85%] h-[85%] rounded-full border-2 border-dashed border-[#9F87D8]/40"></div>
                <div className="absolute w-[60%] h-[60%] rounded-full border-2 border-dashed border-[#C7DCF7]"></div>
                <div className="absolute w-[35%] h-[35%] rounded-full border-2 border-dashed border-[#D4EDDB]"></div>

                <div className="relative w-28 h-28 rounded-full bg-white brutal-shadow flex items-center justify-center">
                  <Globe2 className="w-12 h-12 text-[#9F87D8]" strokeWidth={1.5} />
                </div>

                <div className="absolute top-6 left-10 w-14 h-14 rounded-2xl bg-white brutal-shadow flex items-center justify-center">
                  <span className="text-2xl">🇮🇳</span>
                </div>
                <div className="absolute top-12 right-8 w-14 h-14 rounded-2xl bg-white brutal-shadow flex items-center justify-center">
                  <span className="text-2xl">🇺🇸</span>
                </div>
                <div className="absolute bottom-10 left-6 w-14 h-14 rounded-2xl bg-white brutal-shadow flex items-center justify-center">
                  <span className="text-2xl">🇬🇧</span>
                </div>
                <div className="absolute bottom-8 right-12 w-14 h-14 rounded-2xl bg-white brutal-shadow flex items-center justify-center">
                  <span className="text-2xl">🇦🇪</span>
                </div>
              </div>

              <div className="absolute top-5 right-5 px-3 py-1.5 rounded-full bg-white brutal-shadow-sm text-[10px] font-bold uppercase tracking-[0.2em] text-[#1F2547] inline-flex items-center gap-2">
                <Wifi className="w-3.5 h-3.5 text-[#9F87D8]" /> Live • Online
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-[#9F87D8] mb-3">
              / Learn from anywhere
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-[#1F2547] leading-[1.05]">
              Your classroom is wherever{" "}
              <span className="relative inline-block">
                <span className="relative z-10">you are.</span>
                <span className="absolute inset-x-0 bottom-1 h-3 md:h-4 bg-[#D4EDDB] -z-0 -skew-x-6"></span>
              </span>
            </h2>
            <p className="mt-5 text-lg text-[#5B6079] font-medium max-w-xl leading-relaxed">
              All Percept Academy courses are 100% online and live. Join from
              your living room, your hostel, or a café across the world — all
              you need is a phone or a laptop.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-2xl bg-white brutal-shadow-sm hover-pop">
                <div className="w-10 h-10 rounded-xl bg-[#C7DCF7] flex items-center justify-center mb-3">
                  <MapPin className="w-5 h-5 text-[#1F2547]" />
                </div>
                <h3 className="font-display font-bold text-base text-[#1F2547]">
                  Any city, any country
                </h3>
                <p className="text-sm text-[#5B6079] mt-1 font-medium">
                  Learners from 12+ countries already onboard.
                </p>
              </div>
              <div className="p-5 rounded-2xl bg-white brutal-shadow-sm hover-pop">
                <div className="w-10 h-10 rounded-xl bg-[#D4EDDB] flex items-center justify-center mb-3">
                  <Clock className="w-5 h-5 text-[#1F2547]" />
                </div>
                <h3 className="font-display font-bold text-base text-[#1F2547]">
                  Flexible timings
                </h3>
                <p className="text-sm text-[#5B6079] mt-1 font-medium">
                  Morning, evening & weekend batches.
                </p>
              </div>
              <div className="p-5 rounded-2xl bg-white brutal-shadow-sm hover-pop">
                <div className="w-10 h-10 rounded-xl bg-[#EFE9F7] flex items-center justify-center mb-3">
                  <Wifi className="w-5 h-5 text-[#9F87D8]" />
                </div>
                <h3 className="font-display font-bold text-base text-[#1F2547]">
                  Live + recorded
                </h3>
                <p className="text-sm text-[#5B6079] mt-1 font-medium">
                  Miss a class? Watch it later, anytime.
                </p>
              </div>
              <div className="p-5 rounded-2xl bg-white brutal-shadow-sm hover-pop">
                <div className="w-10 h-10 rounded-xl bg-[#9F87D8]/15 flex items-center justify-center mb-3">
                  <Globe2 className="w-5 h-5 text-[#9F87D8]" />
                </div>
                <h3 className="font-display font-bold text-base text-[#1F2547]">
                  Phone or laptop
                </h3>
                <p className="text-sm text-[#5B6079] mt-1 font-medium">
                  Works on any device with internet.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOGS */}
      {blogs.length > 0 && (
        <section className="bg-[#EFE9F7]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-[#9F87D8] mb-3">
                  / From the Blog
                </span>
                <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-[#1F2547] max-w-2xl">
                  Stories, tips & language deep-dives.
                </h2>
              </div>
              <Link
                to="/blogs"
                data-testid="blogs-view-all"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#1F2547] text-white font-bold rounded-full brutal-shadow-sm btn-press"
              >
                View all blogs <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {blogs.slice(0, 6).map((b) => {
                const isExternal = b.url && /^https?:\/\//i.test(b.url);
                const catClass =
                  HERO_BLOG_CATEGORY_COLOR[b.category] ||
                  "bg-[#D4EDDB] text-[#1F2547]";
                const cardClass =
                  "flex flex-col bg-white rounded-3xl brutal-shadow overflow-hidden hover-pop group";
                const inner = (
                  <>
                    <div className="aspect-[16/9] overflow-hidden bg-[#EFE9F7]">
                      {b.image ? (
                        <img
                          src={b.image}
                          alt={b.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#9F87D8] text-4xl font-black">
                          ✦
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-3 mb-3">
                        {b.category && (
                          <span
                            className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full ${catClass}`}
                          >
                            {b.category}
                          </span>
                        )}
                        {(b.date || b.read_time) && (
                          <span className="text-xs font-medium text-[#5B6079]">
                            {[b.date, b.read_time].filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </div>
                      <h3 className="font-display text-xl font-black tracking-tight text-[#1F2547] leading-snug">
                        {b.title}
                      </h3>
                      {b.excerpt && (
                        <p className="mt-2 text-[#5B6079] font-medium text-sm">
                          {b.excerpt}
                        </p>
                      )}
                      <div className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#9F87D8] group-hover:gap-3 transition-all">
                        Read article <ArrowUpRight className="w-4 h-4" />
                      </div>
                    </div>
                  </>
                );
                if (!b.url) {
                  return (
                    <div
                      key={b.id}
                      data-testid={`home-blog-card-${b.id}`}
                      className={cardClass}
                    >
                      {inner}
                    </div>
                  );
                }
                if (isExternal) {
                  return (
                    <a
                      key={b.id}
                      href={b.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`home-blog-card-${b.id}`}
                      className={cardClass}
                    >
                      {inner}
                    </a>
                  );
                }
                return (
                  <Link
                    key={b.id}
                    to={b.url}
                    data-testid={`home-blog-card-${b.id}`}
                    className={cardClass}
                  >
                    {inner}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div
          data-testid="home-final-cta"
          className="relative rounded-3xl brutal-shadow-lg bg-[#1F2547] text-white p-10 md:p-16 overflow-hidden"
        >
          <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-[#9F87D8] opacity-90"></div>
          <div className="absolute bottom-[-40px] right-32 w-32 h-32 rounded-full bg-[#C7DCF7] opacity-90"></div>
          <div className="absolute top-20 right-72 w-20 h-20 rounded-full bg-[#D4EDDB] opacity-90"></div>

          <div className="relative max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#D4EDDB]">
              / Ready when you are
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-3">
              Your fluent self is waiting.
            </h2>
            <p className="mt-5 text-lg text-white/75 font-medium">
              Book a free 15-min discovery call and we&apos;ll help you choose
              the right course.
            </p>
            <a
              href="https://wa.me/919968110261"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-cta-book"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 bg-white text-[#1F2547] font-bold rounded-full brutal-shadow btn-press"
            >
              Book a free call <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
